import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

type Toast = { id: number; kind: 'success' | 'error' | 'info'; text: string };

const ToastContext = createContext<(kind: Toast['kind'], text: string) => void>(() => {});

/** Minimal daisyUI toasts (bottom end), auto-dismissed after 5 s. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((kind: Toast['kind'], text: string) => {
    const id = Date.now() + Math.random();
    setToasts((list) => [...list, { id, kind, text }]);
    setTimeout(() => setToasts((list) => list.filter((x) => x.id !== id)), 5000);
  }, []);
  const value = useMemo(() => push, [push]);
  return (
    <ToastContext value={value}>
      {children}
      <div className="toast toast-end z-[100]" role="status" aria-live="polite">
        {toasts.map((x) => (
          <div
            key={x.id}
            className={`alert alert-soft ${x.kind === 'success' ? 'alert-success' : x.kind === 'error' ? 'alert-error' : 'alert-info'}`}>
            <span>{x.text}</span>
          </div>
        ))}
      </div>
    </ToastContext>
  );
}

export const useToast = () => useContext(ToastContext);
